import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
} from '@mui/material';
import {
  CheckCircle,
  CalendarToday,
  Info,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface ProductionData {
  api_status: string;
  api_message: string;
  operation_details: {
    handling_type_code: string;
    entity_type: string;
    identifier: string;
    handling_date: string;
    quantity_liters: number;
    destination_tank: string;
    records_processed: number;
  };
  entity_profile: {
    _key: string;
    _id: string;
    _rev: string;
    farm_id: string;
    current_farm_id?: string;
    birth_farm_id?: string;
    name: string;
    cattle_id?: {
      tag: string;
      country_code: string;
      manufacturer_code: number;
      unique_id: string;
    };
    cattle_tags?: string[];
    breed_code?: string;
    gender?: string;
    birth_date?: string;
    birth_weight_kg?: number;
    production_purpose?: string | null;
    dam_cattle_id?: string | null;
    sire_cattle_id?: string | null;
    current_paddock_id?: string;
    death_date?: string | null;
    death_reason?: string | null;
    current_status?: string;
    created_at?: string;
    updated_at?: string;
  };
}

export interface ProductionDetailsData {
  data: ProductionData;
  resumo: string;
  url: string;
}

export interface ProductionDetailsViewProps {
  productionData: ProductionDetailsData;
}

const formatDate = (dateString: string, t?: any) => {
  const date = new Date(dateString);
  const locale = t ? t('common:locale') : 'pt-BR';
  return date.toLocaleDateString(locale);
};

const getStatusColor = (status?: string) => {
  return !status || status.toLowerCase() === 'ativo'
    ? DESIGN_TOKENS.colors.brand.primary
    : DESIGN_TOKENS.colors.status.error[500];
};

const ProductionDetailsView: React.FC<ProductionDetailsViewProps> = ({
  productionData,
}) => {
  const { t } = useTranslation(['dairy', 'common']);
  const { data, resumo } = productionData;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: DESIGN_TOKENS.colors.brand.primary,
            mx: 'auto',
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              bgcolor: 'white',
              maskImage: 'url(/Leite.svg)',
              WebkitMaskImage: 'url(/Leite.svg)',
              maskRepeat: 'no-repeat',
              WebkitMaskRepeat: 'no-repeat',
              maskSize: 'contain',
              WebkitMaskSize: 'contain',
              maskPosition: 'center',
              WebkitMaskPosition: 'center',
            }}
          />
        </Avatar>
        <Typography variant="h4" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.brand.primary, mb: 1 }}>
          {t('dairy:productionDetails.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 2 }}>
          {resumo}
        </Typography>
      </Box>

      {/* Status Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('dairy:productionDetails.operationStatus')}
                </Typography>
                <Chip
                  label={t('dairy:productionDetails.complete')}
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.brand.primary,
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '& .MuiChip-icon': {
                      color: 'white'
                    }
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img 
                src="/milk.svg" 
                alt={t('dairy:productionDetails.animal')} 
                style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
              />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('dairy:productionDetails.milkedAnimal')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {data.entity_profile.name}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info sx={{ color: DESIGN_TOKENS.colors.status.info[500], fontSize: '1.5rem' }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  {t('dairy:productionDetails.recordsProcessed')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {data.operation_details.records_processed}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Production Details */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
        {/* Production Information */}
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Avatar
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.brand.primary,
                    width: 40,
                    height: 40,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'white',
                      maskImage: 'url(/Leite.svg)',
                      WebkitMaskImage: 'url(/Leite.svg)',
                      maskRepeat: 'no-repeat',
                      WebkitMaskRepeat: 'no-repeat',
                      maskSize: 'contain',
                      WebkitMaskSize: 'contain',
                      maskPosition: 'center',
                      WebkitMaskPosition: 'center',
                    }}
                  />
                </Avatar>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.text.primary,
                  }}
                >
                  {t('dairy:productionDetails.productionInformation')}
                </Typography>
              </Box>

              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.quantityProduced')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.brand.primary }}>
                    {(data?.operation_details?.quantity_liters ?? (data as any)?.handling_details?.milk_quantity_liters ?? 0)} {t('dairy:productionDetails.liters')}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontSize: '1.2rem' }} />
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('dairy:productionDetails.milkingDate')}
                    </Typography>
                    <Typography variant="body1">
                      {data?.operation_details?.handling_date ? formatDate(data.operation_details.handling_date, t) : t('dairy:productionDetails.notSpecified')}
                    </Typography>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.destinationTank')}
                  </Typography>
                  <Typography variant="body1">
                    {data.operation_details.destination_tank || t('dairy:productionDetails.notSpecified')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.operationType')}
                  </Typography>
                  <Typography variant="body1">
                    {t('dairy:productionDetails.recordProduction')}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Animal/Lot Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: DESIGN_TOKENS.colors.text.primary }}>
            {productionData.data.operation_details.entity_type === 'lote' ? t('dairy:productionDetails.lotInformation') : t('dairy:productionDetails.animalInformation')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {productionData.data.operation_details.entity_type === 'lote' ? t('dairy:productionDetails.lotName') : t('dairy:productionDetails.animalName')}
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.name}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.animalId')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {data.entity_profile._key}
                  </Typography>
                </Box>

                {data.entity_profile.cattle_id?.tag && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('dairy:productionDetails.rfidTag')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {data.entity_profile.cattle_id.tag}
                    </Typography>
                  </Box>
                )}

                {productionData.data.operation_details.entity_type === 'lote' && data.entity_profile.cattle_tags && (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {t('dairy:productionDetails.lotAnimals')}
                    </Typography>
                    <Typography variant="body1">
                      {data.entity_profile.cattle_tags.join(', ')}
                    </Typography>
                  </Box>
                )}

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.breed')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 600 }}>
                    {data.entity_profile.breed_code}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.gender')}
                  </Typography>
                  <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 600 }}>
                    {data.entity_profile.gender}
                  </Typography>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ flex: '1 1 300px' }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.currentStatus')}
                  </Typography>
                  <Chip
                    label={data.entity_profile.current_status || t('common:status.active')}
                    size="small"
                    sx={{
                      bgcolor: getStatusColor(data.entity_profile.current_status),
                      color: 'white',
                      fontWeight: 600,
                      '& .MuiChip-icon': {
                        color: 'white'
                      }
                    }}
                  />
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.birthDate')}
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.birth_date ? formatDate(data.entity_profile.birth_date, t) : t('dairy:productionDetails.notSpecified')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.birthWeight')}
                  </Typography>
                  <Typography variant="body1">
                    {typeof data.entity_profile.birth_weight_kg === 'number' ? `${data.entity_profile.birth_weight_kg} kg` : t('dairy:productionDetails.notSpecified')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.currentPaddock')}
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.current_paddock_id ? `${t('dairy:productionDetails.paddock')} ${data.entity_profile.current_paddock_id}` : t('dairy:productionDetails.notSpecified')}
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dairy:productionDetails.lastUpdate')}
                  </Typography>
                  <Typography variant="body1">
                    {data.entity_profile.updated_at ? formatDate(data.entity_profile.updated_at, t) : t('dairy:productionDetails.notSpecified')}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductionDetailsView;